import { suggestedWithinLimits } from "~/utils/allocationRuleMaths"
import {
  engineLevels,
  MAX_MIN_DISPLAY_QTY,
  MAX_SETTING_QTY,
  ORDER_MULTIPLE,
  type EngineLine,
  type StoredSuggestedQuantityMeta,
  type ResolvedSetting,
  type SettingDiagnostics,
  type SettingSource,
  type SettingsConflict,
} from "~/utils/suggestedQuantity"

// The minimum, maximum, order multiple and exclusion one store x SKU line runs
// on: the store x SKU value, else the variant value (all stores), else the
// default: no minimum, no maximum, order multiple 1. Exclusion is set per store x SKU only. Pure.

/** A variant's row in AllocationVariantSetting; null leaves the level below to apply. */
export type VariantSettingValues = {
  minDisplayQty: number | null
  maxQty: number | null
  orderMultiple: number | null
}
/** A store x SKU row in AllocationStoreSkuSetting. */
export type StoreSkuSettingValues = VariantSettingValues & { excluded: boolean | null }

export type QtyField = keyof VariantSettingValues
export type SettingField = keyof StoreSkuSettingValues

export const QTY_FIELDS = ["minDisplayQty", "maxQty", "orderMultiple"] as const satisfies QtyField[]
export const SETTING_FIELDS = ["excluded", ...QTY_FIELDS] as const satisfies SettingField[]

/** Integer bounds a saved value takes per field; the save refuses anything outside. */
export const QTY_BOUNDS: Record<QtyField, { min: number; max: number }> = {
  minDisplayQty: { min: 0, max: MAX_MIN_DISPLAY_QTY },
  maxQty: { min: 0, max: MAX_SETTING_QTY },
  orderMultiple: { min: 1, max: MAX_SETTING_QTY },
}

export type StoreSkuCell = { skuId: string; locationId: string }

export const storeSkuKey = ({ skuId, locationId }: StoreSkuCell) => `${skuId}\u0000${locationId}`

export function parseStoreSkuKey(key: string): StoreSkuCell {
  const [skuId = "", locationId = ""] = key.split("\u0000")
  return { skuId, locationId }
}

export type ResolvedLineSettings = Required<
  Pick<EngineLine, "excluded" | "maxQty" | "orderMultiple">
> & { minDisplayQty: number } & SettingDiagnostics

const SPECIFICITY: Record<SettingSource, number> = { storeSku: 2, variant: 1, default: 0 }

function resolve<T>(
  storeSku: T | null | undefined,
  variant: T | null | undefined,
  fallback: T,
): ResolvedSetting<T> {
  if (storeSku != null) return { value: storeSku, source: "storeSku" }
  if (variant != null) return { value: variant, source: "variant" }
  return { value: fallback, source: "default" }
}

type ReconciledMinMax = {
  min: ResolvedSetting<number>
  max: ResolvedSetting<number | null>
  conflict: SettingsConflict | null
}

/**
 * A minimum above the maximum can only come from two levels (a row's CHECK
 * rejects it), and the more specific level wins. An ignored maximum
 * leaves no cap; an ignored minimum leaves no minimum.
 */
function reconcileMinMax(
  min: ResolvedSetting<number>,
  max: ResolvedSetting<number | null>,
): ReconciledMinMax {
  const maxValue = max.value
  if (maxValue == null || min.value <= maxValue) return { min, max, conflict: null }
  if (SPECIFICITY[min.source] > SPECIFICITY[max.source]) {
    return {
      min,
      max: { value: null, source: "default" },
      conflict: { winner: "minDisplayQty", ignored: { value: maxValue, source: max.source } },
    }
  }
  return {
    min: { value: 0, source: "default" },
    max,
    conflict: { winner: "maxQty", ignored: min },
  }
}

/** The settings as the engine takes them, with where each came from for the line's meta. */
export function resolveLineSettings(
  storeSku: StoreSkuSettingValues | undefined,
  variant: VariantSettingValues | undefined,
): ResolvedLineSettings {
  const excluded = resolve(storeSku?.excluded, null, false)
  const { min, max, conflict } = reconcileMinMax(
    resolve(storeSku?.minDisplayQty, variant?.minDisplayQty, 0),
    resolve<number | null>(storeSku?.maxQty, variant?.maxQty, null),
  )
  const orderMultiple = resolve(storeSku?.orderMultiple, variant?.orderMultiple, ORDER_MULTIPLE)
  return {
    excluded: excluded.value,
    minDisplayQty: min.value,
    maxQty: max.value,
    orderMultiple: orderMultiple.value,
    settingSources: {
      excluded: excluded.source,
      minDisplayQty: min.source,
      maxQty: max.source,
      orderMultiple: orderMultiple.source,
    },
    settingsConflict: conflict,
  }
}

/**
 * A stored line re-sized under new settings without a run: its levels stay and the limits are
 * applied again, as suggestQuantity applies them. quantity is null when the run set no levels
 * (excluded, no ROS basis): only the settings on the meta change.
 */
export function reapplyLineSettings(
  meta: StoredSuggestedQuantityMeta,
  settings: ResolvedLineSettings,
): { quantity: number | null; meta: StoredSuggestedQuantityMeta } {
  const { minDisplayQty, maxQty, orderMultiple, settingSources, settingsConflict } = settings
  const withSettings = {
    ...meta,
    mdq: minDisplayQty,
    maxQty,
    orderMultiple,
    settingSources,
    settingsConflict,
  }
  const levels = engineLevels(meta)
  if (levels == null) return { quantity: null, meta: withSettings }
  const { quantity, maxLimit, minimumRaised } = suggestedWithinLimits(levels, levels.position, {
    mdq: minDisplayQty,
    maxQty,
    orderMultiple,
    ros: levels.ros,
    transitDays: levels.transitDays,
  })
  return { quantity, meta: { ...withSettings, maxLimit, minimumRaised } }
}
